import { useState, useEffect, useCallback, useRef } from 'react';
import { db } from 'config/firebase';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  onSnapshot
} from 'firebase/firestore';

// Valid time ranges that correspond to pre-computed BigQuery analyses
const VALID_TIME_RANGES = ['7d', '30d', '60d', '90d'];

const PAGE_SIZE = 50;

/**
 * Priority values for sorting (lower = more urgent)
 */
const PRIORITY_SORT_VALUES = {
  'URGENT': 1,
  'HIGH': 2,
  'MEDIUM': 3,
  'OK': 4
};

/**
 * Calculate a composite business impact score for sorting
 * Higher score = more business impact = should be shown first
 *
 * Factors:
 * - Priority (URGENT=1000, HIGH=750, MEDIUM=500, OK=250)
 * - Sample size confidence (scaled 0-200)
 * - Delta magnitude (scaled 0-100)
 */
export function calculateBusinessImpactScore(rec) {
  // Priority weight (most important)
  const priorityScore = {
    'URGENT': 1000,
    'HIGH': 750,
    'MEDIUM': 500,
    'OK': 250
  }[rec.priority] || 0;

  // Sample size confidence (0-200 points, caps at 100 samples)
  const sampleScore = Math.min(200, (rec.sampleSize || 0) * 2);

  // Delta magnitude (0-100 points, $10 delta = max)
  const deltaScore = Math.min(100, Math.abs(rec.delta || 0) * 10);

  return priorityScore + sampleScore + deltaScore;
}

/**
 * Available sort options for the FC Optimizer table
 * Each option defines:
 *   - field: Firestore field to order by (or 'client' for client-side sort)
 *   - direction: 'asc' or 'desc'
 *   - label: Display label for the dropdown
 *   - clientSort: Optional function for client-side sorting
 *   - group: Category for grouping in dropdown
 */
export const SORT_OPTIONS = {
  // DEFAULT - Business Impact (Priority + Sample Size + Delta)
  businessImpact: {
    field: 'client',
    direction: 'desc',
    label: 'Business Impact (Recommended)',
    group: 'Recommended',
    clientSort: (a, b) => calculateBusinessImpactScore(b) - calculateBusinessImpactScore(a)
  },

  // Priority-based sorts
  priority: {
    field: 'client',
    direction: 'asc',
    label: 'Priority (Urgent First)',
    group: 'Priority',
    clientSort: (a, b) => (PRIORITY_SORT_VALUES[a.priority] || 5) - (PRIORITY_SORT_VALUES[b.priority] || 5)
  },
  priorityDesc: {
    field: 'client',
    direction: 'desc',
    label: 'Priority (OK First)',
    group: 'Priority',
    clientSort: (a, b) => (PRIORITY_SORT_VALUES[b.priority] || 5) - (PRIORITY_SORT_VALUES[a.priority] || 5)
  },

  // Sample size sorts
  sampleSize: {
    field: 'sampleSize',
    direction: 'desc',
    label: 'Sample Size (High to Low)',
    group: 'Confidence',
    clientSort: (a, b) => (b.sampleSize || 0) - (a.sampleSize || 0)
  },
  sampleSizeAsc: {
    field: 'sampleSize',
    direction: 'asc',
    label: 'Sample Size (Low to High)',
    group: 'Confidence',
    clientSort: (a, b) => (a.sampleSize || 0) - (b.sampleSize || 0)
  },

  // Delta sorts
  delta: {
    field: 'delta',
    direction: 'desc',
    label: 'Delta (Largest Increase First)',
    group: 'Delta',
    clientSort: (a, b) => (b.delta || 0) - (a.delta || 0)
  },
  deltaAsc: {
    field: 'delta',
    direction: 'asc',
    label: 'Delta (Largest Decrease First)',
    group: 'Delta',
    clientSort: (a, b) => (a.delta || 0) - (b.delta || 0)
  },
  deltaAbs: {
    field: 'client',
    direction: 'desc',
    label: 'Delta (Largest Change First)',
    group: 'Delta',
    clientSort: (a, b) => Math.abs(b.delta || 0) - Math.abs(a.delta || 0)
  },

  // Financial sorts
  suggestedFC: {
    field: 'suggestedFC',
    direction: 'desc',
    label: 'Suggested FC (High to Low)',
    group: 'Financial',
    clientSort: (a, b) => (b.suggestedFC || 0) - (a.suggestedFC || 0)
  },
  suggestedFCAsc: {
    field: 'suggestedFC',
    direction: 'asc',
    label: 'Suggested FC (Low to High)',
    group: 'Financial',
    clientSort: (a, b) => (a.suggestedFC || 0) - (b.suggestedFC || 0)
  },
  avgCost: {
    field: 'avgCost',
    direction: 'desc',
    label: 'Avg Cost (High to Low)',
    group: 'Financial',
    clientSort: (a, b) => (b.avgCost || 0) - (a.avgCost || 0)
  },

  // SKU sorts
  sku: {
    field: 'sku',
    direction: 'asc',
    label: 'SKU (A-Z)',
    group: 'SKU',
    clientSort: (a, b) => (a.sku || '').localeCompare(b.sku || '')
  },
  skuDesc: {
    field: 'sku',
    direction: 'desc',
    label: 'SKU (Z-A)',
    group: 'SKU',
    clientSort: (a, b) => (b.sku || '').localeCompare(a.sku || '')
  }
};

// Default sort key
export const DEFAULT_SORT_KEY = 'businessImpact';

/**
 * Pure function: apply client-side filters and sort to raw data.
 * Counts are computed pre-priority-filter so summary cards always reflect all priorities.
 */
function applyClientFiltersAndSort(rawData, filters, sortKey) {
  let filtered = rawData;

  // Search filter
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter(r =>
      r.sku?.toLowerCase().includes(searchLower) ||
      r.title?.toLowerCase().includes(searchLower)
    );
  }

  // Strategy filter
  if (filters.strategy && filters.strategy !== '') {
    filtered = filtered.filter(r => r.strategy === filters.strategy);
  }

  // Temperature category filter
  if (filters.temperatureCategory && filters.temperatureCategory !== '') {
    filtered = filtered.filter(r => r.temperatureCategory === filters.temperatureCategory);
  }

  // Service level filter
  if (filters.serviceLevel && filters.serviceLevel !== '') {
    if (filters.serviceLevel === 'EXPEDITED') {
      filtered = filtered.filter(r => r.dominantServiceLevel !== 'GROUND');
    } else {
      filtered = filtered.filter(r => r.dominantServiceLevel === filters.serviceLevel);
    }
  }

  // Compute priority counts BEFORE applying priority filter
  const counts = { urgent: 0, high: 0, medium: 0, ok: 0 };
  filtered.forEach(r => {
    const key = r.priority?.toLowerCase();
    if (counts[key] !== undefined) counts[key]++;
  });

  // Apply priority filter AFTER counting
  if (filters.priority && filters.priority !== 'all') {
    filtered = filtered.filter(r => r.priority === filters.priority);
  }

  // Apply sort
  const sortConfig = SORT_OPTIONS[sortKey] || SORT_OPTIONS.businessImpact;
  if (sortConfig.clientSort) {
    filtered = [...filtered].sort(sortConfig.clientSort);
  }

  return { filtered, counts, totalFiltered: filtered.length };
}

/**
 * Hook to fetch and paginate FC recommendations from fulfillmentCostOpportunity collection
 * and merge with fcBaseline data for current FC values.
 *
 * Architecture:
 * - Firestore fetch (10k limit): runs when server-side filters change (timeRange, analysisType, recommendation) or on refresh
 * - Client-side processing (instant): runs on every filter/sort change against cached data
 * - Pagination: slices from cached filtered array — no network request
 * - Export: returns cached filtered array — guaranteed identical to display
 *
 * @param {object} filters - Filter options (priority, search, recommendation, etc.)
 * @param {object} timeFrame - Time frame for analysis
 * @param {string} sortKey - Sort option key (default: 'businessImpact')
 */
export function useFCRecommendations(filters = {}, timeFrame = null, sortKey = DEFAULT_SORT_KEY) {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [counts, setCounts] = useState({
    urgent: 0,
    high: 0,
    medium: 0,
    ok: 0
  });
  const [totalFilteredCount, setTotalFilteredCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [baselineMap, setBaselineMap] = useState(new Map());
  const [lastAnalysisRun, setLastAnalysisRun] = useState(null);
  const [isRealTime, setIsRealTime] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Cache refs
  const rawDataRef = useRef([]);         // All transformed data from last Firestore fetch
  const allFilteredRef = useRef([]);     // Result of client-side filtering + sorting
  const serverFilterKeyRef = useRef(null); // Last server-side filter key
  const unsubscribeRef = useRef(null);   // Real-time listener cleanup

  // Calculate priority from delta, sample size, and whether FC is set
  const calculatePriority = (rec) => {
    const currentFC = rec.currentFC;
    const delta = Math.abs(rec.delta || 0);
    const sampleSize = rec.sampleSize || rec.totalOrdersPresent || 0;

    // URGENT: No FC set at all, or FC is 0 when suggested is > 0
    if (currentFC === null || (currentFC === 0 && rec.suggestedFC > 1)) {
      return 'URGENT';
    }

    // URGENT: Large delta with high confidence
    if (delta > 5 && sampleSize >= 20) return 'URGENT';
    // HIGH: Significant delta or moderate delta with good sample
    if (delta > 3 || (delta > 1.5 && sampleSize >= 10)) return 'HIGH';
    // MEDIUM: Small delta with some data
    if (delta > 0.5 && sampleSize >= 5) return 'MEDIUM';
    // OK: Minimal change needed
    return 'OK';
  };

  // Transform raw data to recommendation format, merging with baseline
  const transformRecommendation = useCallback((doc, baseline = new Map()) => {
    const data = doc.data ? doc.data() : doc;
    const sku = data.sku;

    const avgCost = parseFloat(data.averageFulfillmentCost) || parseFloat(data.avgCost) || 0;

    // Get current FC from baseline if available, otherwise from the recommendation doc
    const baselineData = baseline.get(sku);
    let currentFC = null;
    if (baselineData !== undefined) {
      currentFC = baselineData.fulfillmentCost;
    } else if (data.currentFC !== undefined && data.currentFC !== null) {
      currentFC = parseFloat(data.currentFC);
    }

    const suggestedFC = parseFloat(data.toBeFulfillmentCost) || parseFloat(data.suggestedFC) || avgCost;

    // Calculate delta: difference between suggested FC and current FC
    // Only calculate when currentFC is set; otherwise delta is null
    const delta = currentFC !== null ? suggestedFC - currentFC : null;
    const sampleSize = parseInt(data.totalOrdersPresent) || parseInt(data.sampleSize) || 0;

    // Always calculate recommendation based on computed delta
    // Don't rely on stored recommendation since currentFC from baseline may differ
    let recommendation;
    if (currentFC === null) {
      recommendation = 'Increase'; // Need to set FC
    } else if (delta > 0.5) {
      recommendation = 'Increase';
    } else if (delta < -0.5) {
      recommendation = 'Decrease';
    } else {
      recommendation = 'No Change';
    }

    // Live stats from real-time updates (computed from atomic counters)
    const liveOrderCount = parseInt(data.liveOrderCount) || 0;
    const liveTotalCost = parseFloat(data.liveTotalCost) || 0;
    const liveSampleSize = liveOrderCount;
    const liveAvgCost = liveOrderCount > 0
      ? Math.round((liveTotalCost / liveOrderCount) * 100) / 100
      : null;
    const liveLastOrderAt = data.liveLastOrderAt?.toDate?.() ||
      (data.liveLastOrderAt ? new Date(data.liveLastOrderAt) : null);

    // Extract service level counts
    const serviceLevelCounts = data.serviceLevelCounts || {};
    const groundCount = serviceLevelCounts.GROUND || 0;
    const twoDayCount = serviceLevelCounts['2-DAY'] || serviceLevelCounts.TWO_DAY || 0;
    const threeDayCount = serviceLevelCounts['3-DAY'] || serviceLevelCounts.THREE_DAY || 0;
    const nextDayCount = serviceLevelCounts['NEXT-DAY'] || serviceLevelCounts.NEXT_DAY || 0;
    const expeditedCount = twoDayCount + threeDayCount + nextDayCount;
    const totalServiceOrders = groundCount + expeditedCount;

    // Determine dominant service level
    let dominantServiceLevel = 'GROUND';
    if (totalServiceOrders > 0) {
      if (expeditedCount > groundCount) {
        if (nextDayCount >= twoDayCount && nextDayCount >= threeDayCount) {
          dominantServiceLevel = 'NEXT_DAY';
        } else if (twoDayCount >= threeDayCount) {
          dominantServiceLevel = 'TWO_DAY';
        } else {
          dominantServiceLevel = 'THREE_DAY';
        }
      }
    }

    const rec = {
      sku,
      title: baselineData?.title || data.title || data.productTitle || null,
      currentFC,
      avgCost,
      suggestedFC,
      delta,
      minCost: parseFloat(data.minFulfillmentCost) || parseFloat(data.minCost) || null,
      maxCost: parseFloat(data.maxFulfillmentCost) || parseFloat(data.maxCost) || null,
      stdDev: parseFloat(data.stdDev) || null,
      sampleSize,
      recommendation,
      confidence: sampleSize >= 20 ? 'HIGH' : sampleSize >= 10 ? 'MEDIUM' : 'LOW',
      // Strategy and category info from BigQuery analysis
      strategy: data.strategy || null,
      temperatureCategory: data.temperatureCategory || 'AMBIENT',
      economyPct: parseFloat(data.economyPct) || 0,
      ss1Count: parseInt(data.ss1Count) || 0,
      // Service level breakdown
      serviceLevelCounts,
      groundCount,
      expeditedCount,
      dominantServiceLevel,
      groundPct: totalServiceOrders > 0 ? Math.round((groundCount / totalServiceOrders) * 100) : 0,
      // FC calculation options
      fcOptions: data.fcOptions || null,
      blendedFC: parseFloat(data.blendedFC) || null,
      // Live stats for real-time updates
      liveSampleSize,
      liveAvgCost,
      liveLastOrderAt,
      // Display sample size: prefer live if available and more recent
      displaySampleSize: liveSampleSize > 0 ? liveSampleSize : sampleSize,
      hasLiveData: liveSampleSize > 0
    };

    rec.priority = calculatePriority(rec);
    return rec;
  }, []);

  // Fetch baseline data from fcBaseline collection
  const fetchBaseline = useCallback(async () => {
    try {
      const baselineSnapshot = await getDocs(collection(db, 'fcBaseline'));
      const newBaselineMap = new Map();
      baselineSnapshot.docs.forEach(doc => {
        const data = doc.data();
        newBaselineMap.set(data.sku, {
          fulfillmentCost: data.fulfillmentCost,
          title: data.title
        });
      });
      setBaselineMap(newBaselineMap);
      return newBaselineMap;
    } catch (err) {
      console.error('Error fetching baseline:', err);
      return new Map();
    }
  }, []);

  // Fetch last analysis run timestamp
  const fetchLastAnalysisRun = useCallback(async () => {
    try {
      const runQuery = query(
        collection(db, 'fcAnalysisRuns'),
        orderBy('runAt', 'desc'),
        limit(1)
      );
      const snapshot = await getDocs(runQuery);
      if (!snapshot.empty) {
        const data = snapshot.docs[0].data();
        setLastAnalysisRun({
          runAt: data.runAt?.toDate?.() || new Date(data.runAt),
          skusAnalyzed: data.skusAnalyzed,
          durationMs: data.durationMs
        });
      }
    } catch (err) {
      console.error('Error fetching last analysis run:', err);
    }
  }, []);

  // Compute server filter key — changes trigger Firestore re-fetch
  const serverFilterKey = JSON.stringify({
    timeRange: timeFrame?.preset || '90d',
    analysisType: filters.analysisType || 'all',
    recommendation: filters.recommendation || 'all'
  });

  // Go to specific page (instant — slices from cached filtered data)
  const goToPage = useCallback((pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    const startIdx = (pageNum - 1) * PAGE_SIZE;
    setRecommendations(allFilteredRef.current.slice(startIdx, startIdx + PAGE_SIZE));
    setCurrentPage(pageNum);
  }, [totalPages]);

  // Setup real-time listener for live updates
  const setupRealTimeListener = useCallback((baseline = null) => {
    // Clean up existing listener
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    const currentBaseline = baseline || baselineMap;
    const baseCollection = collection(db, 'fulfillmentCostOpportunity');

    let constraints = [];

    // Apply time range filter - critical for correct data per time window
    const selectedTimeRange = timeFrame?.preset || '90d';
    constraints.push(where('timeRange', '==', selectedTimeRange));

    // Apply analysisType filter
    if (filters.analysisType && filters.analysisType !== 'all') {
      constraints.push(where('analysisType', '==', filters.analysisType));
    }

    // Apply recommendation filter
    if (filters.recommendation && filters.recommendation !== 'all') {
      constraints.push(where('recommendation', '==', filters.recommendation));
    }

    // Build query - use sortScore for ordering to leverage index
    const q = query(
      baseCollection,
      ...constraints,
      orderBy('sortScore', 'desc'),
      limit(100)  // Limit real-time listener to top 100 for performance
    );

    // Set up real-time listener
    const unsubscribe = onSnapshot(q, (snapshot) => {
      let transformed = snapshot.docs.map(doc =>
        transformRecommendation(doc.data(), currentBaseline)
      );

      // Apply client-side filtering for search, priority, strategy, and temperature category
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        transformed = transformed.filter(r =>
          r.sku?.toLowerCase().includes(searchLower) ||
          r.title?.toLowerCase().includes(searchLower)
        );
      }

      if (filters.priority && filters.priority !== 'all') {
        transformed = transformed.filter(r => r.priority === filters.priority);
      }

      // Strategy filter (client-side)
      if (filters.strategy && filters.strategy !== '') {
        transformed = transformed.filter(r => r.strategy === filters.strategy);
      }

      // Temperature category filter (client-side)
      if (filters.temperatureCategory && filters.temperatureCategory !== '') {
        transformed = transformed.filter(r => r.temperatureCategory === filters.temperatureCategory);
      }

      // Service level filter (client-side)
      if (filters.serviceLevel && filters.serviceLevel !== '') {
        if (filters.serviceLevel === 'EXPEDITED') {
          transformed = transformed.filter(r => r.dominantServiceLevel !== 'GROUND');
        } else {
          transformed = transformed.filter(r => r.dominantServiceLevel === filters.serviceLevel);
        }
      }

      // Apply client-side sorting
      const sortConfig = SORT_OPTIONS[sortKey] || SORT_OPTIONS.businessImpact;
      if (sortConfig.clientSort) {
        transformed.sort(sortConfig.clientSort);
      }

      // Paginate
      const startIdx = (currentPage - 1) * PAGE_SIZE;
      const paginated = transformed.slice(startIdx, startIdx + PAGE_SIZE);

      setRecommendations(paginated);
      setTotalPages(Math.ceil(transformed.length / PAGE_SIZE));
      setLoading(false);
    }, (error) => {
      console.error('Real-time listener error:', error);
      setError(error);
      setLoading(false);
    });

    unsubscribeRef.current = unsubscribe;
  }, [filters, sortKey, currentPage, transformRecommendation, baselineMap, timeFrame]);

  // Refresh data (force re-fetch from Firestore)
  const refresh = useCallback(() => {
    serverFilterKeyRef.current = null;
    setRefreshKey(k => k + 1);
  }, []);

  /**
   * Fetch ALL filtered recommendations for export.
   * Returns the same cached filtered data as the UI displays — guaranteed identical.
   * Falls back to fresh Firestore fetch if cache is empty.
   */
  const fetchAllForExport = useCallback(async () => {
    if (allFilteredRef.current.length > 0) {
      return allFilteredRef.current;
    }

    // Fallback: fresh fetch if cache is somehow empty
    try {
      const currentBaseline = baselineMap.size > 0 ? baselineMap : await fetchBaseline();
      const baseCollection = collection(db, 'fulfillmentCostOpportunity');
      const constraints = [];
      const selectedTimeRange = timeFrame?.preset || '90d';
      constraints.push(where('timeRange', '==', selectedTimeRange));
      if (filters.analysisType && filters.analysisType !== 'all') {
        constraints.push(where('analysisType', '==', filters.analysisType));
      }
      if (filters.recommendation && filters.recommendation !== 'all') {
        constraints.push(where('recommendation', '==', filters.recommendation));
      }
      const q = query(baseCollection, ...constraints, orderBy('sku', 'asc'), limit(10000));
      const snapshot = await getDocs(q);
      const rawData = snapshot.docs.map(d => transformRecommendation(d.data(), currentBaseline));
      const { filtered } = applyClientFiltersAndSort(rawData, filters, sortKey);
      return filtered;
    } catch (err) {
      console.error('Error fetching all for export:', err);
      throw err;
    }
  }, [filters, sortKey, timeFrame, transformRecommendation, baselineMap, fetchBaseline]);

  // Main effect: fetches from Firestore when server filters change,
  // reprocesses cached data when client filters/sort change
  useEffect(() => {
    let cancelled = false;

    // Real-time mode: delegate to snapshot listener
    if (isRealTime) {
      const startRealTime = async () => {
        const baseline = baselineMap.size > 0 ? baselineMap : await fetchBaseline();
        if (cancelled) return;
        setupRealTimeListener(baseline);
      };
      startRealTime();
      return () => {
        cancelled = true;
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
      };
    }

    // Standard mode: fetch + cache + client-side processing
    const run = async () => {
      const needsServerFetch = serverFilterKeyRef.current !== serverFilterKey;

      if (needsServerFetch) {
        setLoading(true);
        setError(null);
        try {
          const baseline = await fetchBaseline();
          if (cancelled) return;

          // Fire off analysis run fetch in parallel (independent read)
          fetchLastAnalysisRun();

          const baseCollection = collection(db, 'fulfillmentCostOpportunity');
          const constraints = [];
          const selectedTimeRange = timeFrame?.preset || '90d';
          constraints.push(where('timeRange', '==', selectedTimeRange));

          if (filters.analysisType && filters.analysisType !== 'all') {
            constraints.push(where('analysisType', '==', filters.analysisType));
          }
          if (filters.recommendation && filters.recommendation !== 'all') {
            constraints.push(where('recommendation', '==', filters.recommendation));
          }

          const q = query(baseCollection, ...constraints, orderBy('sku', 'asc'), limit(10000));
          const snapshot = await getDocs(q);
          if (cancelled) return;

          rawDataRef.current = snapshot.docs.map(d => transformRecommendation(d.data(), baseline));
          serverFilterKeyRef.current = serverFilterKey;
        } catch (err) {
          if (cancelled) return;
          console.error('Error fetching recommendations:', err);
          setError(err);
          setLoading(false);
          return;
        }
      }

      // Apply client-side filters and sort to cached raw data
      const { filtered, counts: newCounts, totalFiltered } = applyClientFiltersAndSort(
        rawDataRef.current, filters, sortKey
      );
      if (cancelled) return;

      allFilteredRef.current = filtered;
      setCounts(newCounts);
      setTotalFilteredCount(totalFiltered);
      const pages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));
      setTotalPages(pages);
      setRecommendations(filtered.slice(0, PAGE_SIZE));
      setCurrentPage(1);
      setLoading(false);
    };

    run();

    return () => {
      cancelled = true;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [serverFilterKey, filters.search, filters.priority, filters.strategy,
      filters.temperatureCategory, filters.serviceLevel, sortKey, refreshKey, isRealTime]);

  return {
    recommendations,
    loading,
    error,
    counts,
    totalFilteredCount,
    refresh,
    currentPage,
    totalPages,
    goToPage,
    // Sort information
    currentSort: sortKey,
    availableSorts: SORT_OPTIONS,
    // Last analysis run info
    lastAnalysisRun,
    // Real-time status
    isRealTime,
    setIsRealTime,
    // Export function (returns cached filtered data — identical to display)
    fetchAllForExport
  };
}

export default useFCRecommendations;
